import { useState } from 'preact/hooks';
import { gregOf, hijriName, isDecided, monthKey, monthStart, nextMonth, prevMonth, sunsetOn } from '../astro/hijri';
import { dayMonth, longDate } from '../lib/hijriAlerts';
import { addDays } from '../lib/localtime';
import { alertState, enableAlerts } from '../live/push';
import { hijriNow, saudiStarts, takeStarts } from '../live/saudi';
import { panel, place, settings } from '../state';
import { Segmented, Sheet } from './chrome';

// The owner's own page, reached only by its address (?owner) or by tapping "About Ahillah" in Settings seven times: it
// registers this device, with the owner's key, to hear each time Saudi Arabia's date for a new month is applied
// (server/watch.ts), and lets the owner set a month's start where the two sources disagree. The key goes to Ahillah's
// server each time, to be checked, and is never kept on the phone.

const RIYADH = { lat: 24.7136, lon: 46.6753, tz: 'Asia/Riyadh' };

/** "Tue 17 Feb". */
const shortDate = (d: string) => {
  const [w, n, m] = dayMonth(d).split(' ');
  return `${w.slice(0, 3)} ${n} ${m.slice(0, 3)}`;
};

export function OwnerPanel() {
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const register = async () => {
    setBusy(true);
    setNote(null);
    try {
      // The device's own notification address: notifications must be on (an iPhone asks, after this tap).
      if (alertState.peek() !== 'on') await enableAlerts();
      const reg = 'serviceWorker' in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
      const sub = await reg?.pushManager.getSubscription();
      if (!sub) return setNote('Notifications are not on for Ahillah on this device. Turn them on in Settings, then try again.');
      // With this device's "Play a sound": the owner's notes follow it (and, through this device's alerts, any change to it).
      const r = await fetch('/api/owner', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, sub: sub.toJSON(), sound: settings.peek().pushSound }) });
      // Registered only when the push service took the first note to this device (the server says so in its answer).
      const done = r.ok && ((await r.json().catch(() => null)) as { ok?: unknown } | null)?.ok === true;
      setNote(
        done ? 'Registered. A notification should arrive in a few seconds.'
        : r.status === 403 ? 'That key was not accepted.'
        : r.status === 429 ? 'Too many tries today. Try again tomorrow.'
        : r.status === 410 ? 'This device’s alert address has expired: turn alerts off and on again in Settings, then register it again.'
        : 'Ahillah’s server could not register this device just now.',
      );
    } catch {
      setNote('Ahillah’s server could not be reached.');
    } finally {
      setKey('');
      setBusy(false);
    }
  };
  return (
    <Sheet onClose={() => (panel.value = null)} label="For the owner">
      <h2 class="sheet-title">For the owner</h2>
      <p class="sheet-lead">Register this device to hear each time Saudi Arabia’s date for a new month is applied.</p>
      <form class="owner-form" onSubmit={(e) => { e.preventDefault(); if (key && !busy) void register(); }}>
        <input type="password" autocomplete="off" spellcheck={false} value={key} onInput={(e) => setKey((e.target as HTMLInputElement).value)} placeholder="Owner key" aria-label="Owner key" />
        <button class="btn" type="submit" disabled={!key || busy}>{busy ? 'Registering…' : 'Register this device'}</button>
      </form>
      {note && <p class="fine" role="status">{note}</p>}
      <SetMonth />
    </Sheet>
  );
}

/** A month's 1st set by the owner, for a month the two sources disagree on: last month, this month, or next month once
 *  its crescent has been looked for (the server holds to the same), on Umm al-Qurá's day or the day either side. A
 *  month not yet decided has no day chosen until the owner chooses one. The Ministry's list never changes a start the
 *  owner has set. */
function SetMonth() {
  const starts = saudiStarts.value;
  const cur = hijriNow(Date.now(), place.value, starts);
  const curMonth = { month: cur.month, year: cur.year };
  const looked = Date.now() >= sunsetOn(addDays(monthStart(curMonth, starts), 28), RIYADH);
  const months = [prevMonth(cur), curMonth, ...(looked ? [nextMonth(cur)] : [])];
  const [which, setWhich] = useState(1);
  const m = months[Math.min(which, months.length - 1)];
  const uq = gregOf({ ...m, day: 1 });
  const days = [addDays(uq, -1), uq, addDays(uq, 1)];
  const applied = monthStart(m, starts);
  const decided = isDecided(m, starts);
  const [picked, setPicked] = useState<string | null>(null);
  const date = picked && days.includes(picked) ? picked : decided ? applied : null;
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setNote(null);
    try {
      if (!date) return;
      const r = await fetch('/api/owner/set', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ key, month: monthKey(m), date }) });
      if (r.ok) {
        takeStarts(((await r.json()) as { starts?: unknown }).starts);
        setNote(`Set: 1 ${hijriName(m)} is ${longDate(date)}. Phones take it the next time they check.`);
      } else setNote(r.status === 403 ? 'That key was not accepted.' : r.status === 429 ? 'Too many tries today. Try again tomorrow.' : r.status === 409 ? 'Not yet: Saudi Arabia has not looked for its crescent.' : 'Ahillah’s server could not set it just now.');
    } catch {
      setNote('Ahillah’s server could not be reached.');
    } finally {
      setKey('');
      setBusy(false);
    }
  };
  return (
    <>
      <h3 class="section-title">Set a month’s start</h3>
      <p class="fine">Only for a month the two sources disagree on. Umm al-Qurá has the 1st of {hijriName(m)} on {longDate(uq)}; phones now have {longDate(applied)}.{decided ? '' : ' Not yet announced: setting it means phones stop waiting for Saudi Arabia’s announcement.'}</p>
      <div class="owner-form">
        <Segmented label="Month" value={which} options={months.map((x, i) => [i, hijriName(x).replace(/ \d+$/, '')] as [number, string])} onChange={(v) => { setWhich(v); setPicked(null); setNote(null); }} />
        <Segmented label="Its 1st" value={date ?? ''} options={days.map((d) => [d, shortDate(d)] as [string, string])} onChange={(v) => { setPicked(v); setNote(null); }} />
      </div>
      <form class="owner-form" onSubmit={(e) => { e.preventDefault(); if (key && !busy) void send(); }}>
        <input type="password" autocomplete="off" spellcheck={false} value={key} onInput={(e) => setKey((e.target as HTMLInputElement).value)} placeholder="Owner key" aria-label="Owner key, to set the date" />
        <button class="btn" type="submit" disabled={!key || busy || !date}>{busy ? 'Setting…' : date ? `Set 1 ${hijriName(m)}: ${shortDate(date)}` : 'Choose its 1st'}</button>
      </form>
      {note && <p class="fine" role="status">{note}</p>}
    </>
  );
}
